import { useEffect } from "react";
import * as React from "react";

import NodeController05ABI from "@abi/NodeController05";
import AlertBox from "@components/AlertBox";
import ExternalLink from "@components/ExternalLink";
import defaultConfig from "@config";
import useClaimTaxQuote from "@hooks/useClaimTaxQuote";
import useContract from "@hooks/useContract";
import useToast from "@hooks/useToast";
import type { MyLocale } from "@i18n";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import formatRewards from "@utils/formatRewards";
import { useI18n } from "next-rosetta";
import { useCallback } from "react";
import type { Address } from "viem";
import { useSimulateContract, useWriteContract } from "wagmi";

const useClaim = (nodeIDs) => {
	const { nodeController05Contract } = useContract();
	const claimFee = useClaimTaxQuote(nodeIDs);
	const { data } = useSimulateContract({
		abi: NodeController05ABI,
		address: nodeController05Contract.address as Address,
		args: [
			nodeIDs.map((num: number) =>
				typeof num === "bigint" ? num : BigInt(num?.toString()),
			),
		],
		functionName: "claim",
		gas: BigInt("1500000"),
		value: BigInt(claimFee),
	});
	const {
		isPending: isLoading,
		reset,
		status,
		writeContract,
	} = useWriteContract();

	const write = useCallback(() => {
		if (data?.request) {
			return writeContract(data?.request);
		}
	}, [data]);
	const state = status;

	return { isLoading, resetState: reset, send: write, state, status };
};

export default function ClaimAll({ nodeIDs, open, setOpen }) {
	const i18n = useI18n<MyLocale>();
	const { t } = i18n;
	const {
		isLoading,
		resetState,
		send: claim,
		state,
		status,
	} = useClaim(nodeIDs);

	const { contractFunctionToast } = useToast();
	const claimFee = useClaimTaxQuote(nodeIDs);

	const handleClaim = () => {
		claim();
		resetState;
	};

	const handleClose = useCallback(() => setOpen(false), [setOpen]);

	useEffect(() => {
		contractFunctionToast("Claim", state);
	}, [state, contractFunctionToast]);

	useEffect(() => {
		if (status && status === "success") {
			handleClose();
		}
	}, [status, handleClose]);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			PaperProps={{
				sx: {
					backgroundImage: "unset",
					borderRadius: 5,
				},
			}}
		>
			<DialogTitle>{t("modals.claimAll.title")}</DialogTitle>
			<DialogContent sx={{ minWidth: 280 }}>
				<AlertBox>
					{t("modals.claimAll.content")}{" "}
					<ExternalLink href={defaultConfig.social.discord} warning>
						Discord
					</ExternalLink>
				</AlertBox>
				{!defaultConfig.isDisabled.claim && (
					<>
						{nodeIDs?.length > 6 && (
							<Box sx={{ marginTop: 2 }}>
								<AlertBox>{t("modals.claimAll.moreThanXWarning")}</AlertBox>
							</Box>
						)}
						<ul>
							<li>
								{t("modals.claimAll.optionFee1", {
									avaxFee: formatRewards(BigInt(claimFee), 3),
								})}
							</li>
						</ul>
					</>
				)}
			</DialogContent>
			<DialogActions
				sx={{
					justifyContent: "space-between",
					paddingBottom: 4,
					px: 4,
				}}
			>
				<Button onClick={handleClose} variant="outlined" color="error">
					{t("modals.claimAll.cancel")}
				</Button>

				<LoadingButton
					variant="contained"
					color="primary"
					onClick={handleClaim}
					loading={isLoading}
					disabled={defaultConfig.isDisabled.claim}
				>
					{t("modals.claimAll.continue")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
